import React, { useState, useEffect } from "react";
import { supabase } from "../lib/supabase";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { useTheme } from "../contexts/ThemeContext";

const ContactPage = () => {
  const { theme } = useTheme();
  const [showPopup, setShowPopup] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    service: '',
    message: ''
  });

  useEffect(() => {
    // Show popup after a short delay when page loads
    const timer = setTimeout(() => {
      setShowPopup(true);
    }, 500);
    return () => clearTimeout(timer);
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const { error } = await supabase
        .from('contact_submissions')
        .insert([formData]);

      if (error) throw error;

      try {
        const emailResponse = await fetch('/api/contact-email', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(formData),
        });
        if (!emailResponse.ok) throw new Error('Email notification request failed');
      } catch (emailError) {
        // The database submission is the source of truth; email is a notification layer.
        console.warn('Contact submission saved, but email notification failed:', emailError);
      }

      setSubmitStatus('success');
      setFormData({ name: '', email: '', service: '', message: '' });
      
      // Reset status after 3 seconds
      setTimeout(() => setSubmitStatus('idle'), 3000);
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.id]: e.target.value
    }));
  };

  const closePopup = () => {
    setShowPopup(false);
  };

  const isLightTheme = theme === 'light';
  const contactAccentGradient = isLightTheme
    ? 'linear-gradient(to right, #0f766e, #38bdf8)'
    : 'linear-gradient(to right, #f97316, #f59e0b)';
  const contactAccentColor = isLightTheme ? '#0f766e' : '#f97316';
  const contactAccentGlow = isLightTheme ? '0 0 36px rgba(20,184,166,0.2)' : '0 0 38px rgba(249,115,22,0.24)';
  const contactShellStyle: React.CSSProperties = {
    background: isLightTheme
      ? 'radial-gradient(circle at 78% 10%, rgba(20,184,166,0.24), transparent 32%), radial-gradient(circle at 14% 74%, rgba(56,189,248,0.14), transparent 30%), linear-gradient(135deg, #f8fafc 0%, #edfafa 48%, #f7fbfb 100%)'
      : 'radial-gradient(circle at 78% 10%, rgba(249,115,22,0.24), transparent 32%), radial-gradient(circle at 14% 74%, rgba(245,158,11,0.12), transparent 30%), linear-gradient(135deg, #050505 0%, #0b0b0d 52%, #17100b 100%)',
    ['--contact-panel' as string]: isLightTheme ? 'rgba(255,255,255,0.74)' : 'rgba(5,5,5,0.66)',
    ['--contact-panel-strong' as string]: isLightTheme ? 'rgba(255,255,255,0.88)' : 'rgba(0,0,0,0.74)',
    ['--contact-muted' as string]: isLightTheme ? 'rgba(8,47,73,0.72)' : 'rgba(255,237,213,0.72)',
    ['--contact-border' as string]: isLightTheme ? 'rgba(20,184,166,0.32)' : 'rgba(251,146,60,0.3)',
  };
  const accentGlowClass = isLightTheme ? 'bg-teal-400/18' : 'bg-orange-500/14';
  const contactPanelClass = isLightTheme
    ? 'border-teal-400/30 bg-white/74 shadow-[0_0_48px_rgba(20,184,166,0.14),0_28px_90px_rgba(8,47,73,0.08)]'
    : 'border-orange-300/25 bg-black/62 shadow-[0_0_52px_rgba(249,115,22,0.14),0_28px_90px_rgba(0,0,0,0.42)]';
  const contactSolidPanelClass = isLightTheme
    ? 'border-teal-400/28 bg-white shadow-[0_0_44px_rgba(20,184,166,0.12),0_24px_80px_rgba(8,47,73,0.1)]'
    : 'border-orange-300/24 bg-[#080604] shadow-[0_0_48px_rgba(249,115,22,0.12),0_24px_80px_rgba(0,0,0,0.46)]';
  const fieldClass = isLightTheme
    ? 'border-teal-400/28 bg-white/82 text-slate-950 placeholder:text-slate-400 shadow-[inset_0_1px_0_rgba(255,255,255,0.82)] focus-visible:ring-teal-400'
    : 'border-orange-300/24 bg-black/42 text-orange-50 placeholder:text-orange-100/35 shadow-[inset_0_1px_0_rgba(255,237,213,0.08)] focus-visible:ring-orange-400';
  const labelClass = isLightTheme ? 'text-teal-900/78' : 'text-orange-50/76';
  const noteIconClass = isLightTheme
    ? 'border border-teal-400/30 bg-gradient-to-br from-teal-300 to-sky-300 shadow-[0_0_28px_rgba(20,184,166,0.22)]'
    : 'border border-orange-300/30 bg-gradient-to-br from-orange-500 to-amber-300 shadow-[0_0_28px_rgba(249,115,22,0.24)]';
  const submitButtonClass = isLightTheme
    ? 'w-full border border-teal-100/50 px-6 py-4 font-black uppercase tracking-[0.16em] text-black shadow-[0_0_28px_rgba(20,184,166,0.18)] transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-50 md:w-auto'
    : 'w-full border border-orange-100/20 px-6 py-4 font-black uppercase tracking-[0.16em] text-black shadow-[0_0_28px_rgba(249,115,22,0.18)] transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-50 md:w-auto';

  return (
    <div className="relative min-h-screen w-full overflow-hidden px-4 pb-16 pt-28" style={contactShellStyle}>
      <div className={`pointer-events-none absolute left-1/2 top-0 h-80 w-80 -translate-x-1/2 rounded-full blur-3xl ${accentGlowClass}`} />
      <div className={`pointer-events-none absolute bottom-10 right-[-6rem] h-96 w-96 rounded-full blur-3xl ${accentGlowClass}`} />
      <div className="relative z-10 flex w-full flex-col items-center">
      
      {/* Popup Modal */}
      {showPopup && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/68 p-4">
          <div className={`relative w-full max-w-2xl overflow-hidden border p-8 ${contactSolidPanelClass}`}>
            <div className={`pointer-events-none absolute -right-16 -top-16 h-48 w-48 rounded-full blur-3xl ${accentGlowClass}`} />
            {/* Close Button */}
            <button
              onClick={closePopup}
              className={`absolute right-4 top-4 z-10 flex h-8 w-8 items-center justify-center border transition-colors ${isLightTheme ? 'border-teal-400/30 bg-white/70 text-teal-900 hover:bg-teal-50' : 'border-orange-300/25 bg-black/40 text-orange-50 hover:bg-orange-500/15'}`}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            
            {/* Popup Content */}
            <div className="relative z-10 flex flex-col items-center gap-6 md:flex-row">
                             <div className="flex-shrink-0">
                 <div className={`flex h-20 w-20 items-center justify-center ${noteIconClass}`}>
                   <span className="text-2xl" style={{ filter: 'hue-rotate(var(--dino-hue, 0deg))' }}>🦕</span>
                 </div>
               </div>
                             <div className="flex-1 text-center md:text-left">
                 <h3 className="text-xl md:text-2xl font-bold mb-3" style={{ color: isLightTheme ? '#06131a' : '#fff7ed' }}>
                   A Note About Our "Old School" Approach
                 </h3>
                                                     <p className="text-sm md:text-base font-bold mb-3" style={{ color: contactAccentColor }}>
                    We do not edit, alter, or fix other people's codebases.
                  </p>
                  <p className="text-sm md:text-base leading-relaxed mb-3" style={{ color: 'var(--contact-muted)' }}>
                    <strong>We believe in building from the ground up.</strong> While platforms like <strong>WordPress, Wix</strong> and <strong>Squarespace</strong> have their place, we exclusively create custom-coded, end-to-end websites using modern technologies and methodologies.
                  </p>
                  <p className="text-sm md:text-base font-bold mb-3" style={{ color: contactAccentColor }}>
                    This is a developer service - we write code, not drag-and-drop templates.
                  </p>
                  <p className="text-sm md:text-base leading-relaxed mb-3" style={{ color: 'var(--contact-muted)' }}>
                    This means every line of code is purposefully written for your unique needs, resulting in faster performance, complete customization, and future-proof solutions that truly represent your brand. <strong>It's the craftsman's approach in a digital world.</strong>
                  </p>
                  <p className="text-sm md:text-base font-bold italic" style={{ color: contactAccentColor }}>
                    That being said, we can't wait to hear from you!
                  </p>
               </div>
            </div>
          </div>
        </div>
      )}
      <p className={`mb-4 inline-flex w-full max-w-5xl text-[11px] font-black uppercase tracking-[0.28em] ${isLightTheme ? 'text-teal-700' : 'text-orange-300'}`}>
        Start the conversation
      </p>
      <h1 className="mb-4 w-full max-w-5xl text-left text-4xl font-black tracking-tight md:text-6xl" style={{ color: contactAccentColor, textShadow: contactAccentGlow }}>
        Contact Us
      </h1>
      <p className="mb-8 w-full max-w-3xl text-left text-xl font-semibold md:text-2xl" style={{ color: 'var(--contact-muted)' }}>
        Tell us what you want to build. We will read it properly, think about it, and come back with a clear next step.
      </p>
      {/* Status Messages */}
      {submitStatus === 'success' && (
        <div className="mb-6 w-full max-w-3xl border border-emerald-400/35 bg-emerald-500/12 p-4 text-sm font-bold text-emerald-200 shadow-[0_0_24px_rgba(16,185,129,0.12)]">
          ✅ Message sent successfully! We'll get back to you soon.
        </div>
      )}
      {submitStatus === 'error' && (
        <div className="mb-6 w-full max-w-3xl border border-red-400/35 bg-red-500/12 p-4 text-sm font-bold text-red-200 shadow-[0_0_24px_rgba(239,68,68,0.12)]">
          ❌ There was an error sending your message. Please try again.
        </div>
      )}

      <form onSubmit={handleSubmit} className={`relative w-full max-w-3xl space-y-6 overflow-hidden border p-6 md:p-8 ${contactPanelClass}`}>
        <div className={`pointer-events-none absolute -left-20 top-0 h-56 w-56 rounded-full blur-3xl ${accentGlowClass}`} />
        <div>
          <Label htmlFor="name" className={`mb-2 block text-sm font-black uppercase tracking-[0.14em] ${labelClass}`}>
            Name
          </Label>
          <Input
            id="name"
            type="text"
            value={formData.name}
            onChange={handleInputChange}
            required
            className={fieldClass}
          />
        </div>
        <div>
          <Label htmlFor="email" className={`mb-2 block text-sm font-black uppercase tracking-[0.14em] ${labelClass}`}>
            Email
          </Label>
          <Input
            id="email"
            type="email"
            value={formData.email}
            onChange={handleInputChange}
            required
            className={fieldClass}
          />
        </div>
        <div>
          <label htmlFor="service" className={`mb-2 block text-sm font-black uppercase tracking-[0.14em] ${labelClass}`}>
            I need a:
          </label>
          <select
            id="service"
            value={formData.service}
            onChange={handleInputChange}
            required
            className={`w-full border px-4 py-3 font-semibold transition-colors focus:outline-none focus:ring-2 ${fieldClass}`}
          >
            <option className="bg-white text-slate-950" value="">Select a service...</option>
            <option className="bg-white text-slate-950" value="website">Website</option>
            <option className="bg-white text-slate-950" value="web-app-pwa">Web app - PWA</option>
            <option className="bg-white text-slate-950" value="digital-graphic-design">Digital Graphic Design</option>
            <option className="bg-white text-slate-950" value="print-graphic-design">Print Graphic Design</option>
            <option className="bg-white text-slate-950" value="automated-ai-business-solution">Automated AI business solution</option>
            <option className="bg-white text-slate-950" value="cellphone-app">Cellphone app</option>
            <option className="bg-white text-slate-950" value="other">Other / General Inquiry</option>
          </select>
        </div>
        <div>
          <Label htmlFor="message" className={`mb-2 block text-sm font-black uppercase tracking-[0.14em] ${labelClass}`}>
            Message
          </Label>
          <Textarea
            id="message"
            value={formData.message}
            onChange={handleInputChange}
            required
            className={fieldClass}
          />
        </div>
        <button
          type="submit"
          disabled={isSubmitting}
          className={submitButtonClass}
          style={{ background: contactAccentGradient }}
        >
          {isSubmitting ? 'Sending...' : 'Send Message'}
        </button>
      </form>
      
      {/* Old School Tech Message */}
      <div className={`mt-16 w-full max-w-3xl overflow-hidden border p-8 ${contactSolidPanelClass}`}>
        <div className="flex flex-col md:flex-row items-center gap-6">
                     <div className="flex-shrink-0">
             {/* Placeholder for dinosaur image */}
             <div className={`flex h-24 w-24 items-center justify-center ${noteIconClass}`}>
               <span className="text-3xl" style={{ filter: 'hue-rotate(var(--dino-hue, 0deg))' }}>🦕</span>
             </div>
           </div>
                     <div className="flex-1 text-center md:text-left">
            <h3 className="text-xl md:text-2xl font-bold mb-3" style={{ color: isLightTheme ? '#06131a' : '#fff7ed' }}>
               A Note About Our "Old School" Approach
             </h3>
                                         <p className="text-base md:text-lg font-bold mb-4" style={{ color: contactAccentColor }}>
                We do not edit, alter, or fix other people's codebases.
              </p>
              <p className="text-base md:text-lg leading-relaxed mb-4" style={{ color: 'var(--contact-muted)' }}>
                <strong>We believe in building from the ground up.</strong> While platforms like <strong>WordPress, Wix</strong> and <strong>Squarespace</strong> have their place, we exclusively create custom-coded, end-to-end websites using modern technologies and methodologies.
              </p>
              <p className="text-base md:text-lg font-bold mb-4" style={{ color: contactAccentColor }}>
                This is a developer service - we write code, not drag-and-drop templates.
              </p>
              <p className="text-base md:text-lg leading-relaxed mb-4" style={{ color: 'var(--contact-muted)' }}>
                This means every line of code is purposefully written for your unique needs, resulting in faster performance, complete customization, and future-proof solutions that truly represent your brand. <strong>It's the craftsman's approach in a digital world.</strong>
              </p>
              <p className="text-base md:text-lg font-bold italic" style={{ color: contactAccentColor }}>
                That being said, we can't wait to hear from you!
              </p>
           </div>
        </div>
      </div>
      </div>
    </div>
  );
};

export default ContactPage;

